import { useState } from "react";
import {
  Box,
  DataTable,
  DataTableEmpty,
  Icon,
  MenuItem,
  Text,
  type Omittable,
} from "@salestrak/ui";

import {
  useIssueMediaColumns,
  useIssueMediaGroups,
  useIssueMediaRows,
} from "#app/components/issue-view/issue-media-columns";
import { MediaLightbox } from "#app/components/media/media-lightbox";
import type { IssueMediaItem } from "#app/hooks/issue-view/use-issue-media-items";
import { useMediaDownloads } from "#app/lib/hooks/use-media-downloads";

/**
 * The Attachments module's body, shared by the Project and Account summaries:
 * the first N media rows in the shared media table, each opening the lightbox,
 * with per-source quick-links falling back to the host's media tab. The host
 * resolves `items` once (field photos + note attachments, merged) — this body
 * never runs its own media query.
 */
export function AttachmentsWidgetBody({
  entityId,
  entityName,
  items,
  limit,
  onNavigateFallback,
}: Omittable<{
  entityId: string;
  entityName?: string;
  items: IssueMediaItem[];
  limit: number;
  /** Where a quick-link lands when its source row cannot be reached from the
   *  widget — the host's media tab. */
  onNavigateFallback: () => void;
}>) {
  const { rows, lightboxMedia } = useIssueMediaRows({ items });
  const visibleRows = rows.slice(0, limit);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const { downloadPhoto } = useMediaDownloads(entityName);
  const columns = useIssueMediaColumns({
    onOpenLightbox: setLightboxIndex,
    onNavigateFallback,
    tileSize: 24,
  });
  const groups = useIssueMediaGroups(visibleRows, entityId);

  if (items.length === 0) {
    return (
      <Box paddingY="sm">
        <Text size="sm" variant="muted">No attachments yet</Text>
      </Box>
    );
  }

  return (
    <Box gap="sm">
      <Box direction="column" border rounded="sm" overflow="hidden">
        <DataTable
          columns={columns}
          groups={groups}
          getRowId={(row, index) => `${row.photoId}-${index}`}
          density="compact"
          scrollMode="fit"
          paddingX="xs"
          onRowClick={(row) => setLightboxIndex(row.original.index)}
          renderRowActions={(row) => (
            <MenuItem onClick={() => downloadPhoto(row.photoId, row.index + 1, row.item.fileName)}>
              <Icon name="download" size="md" />
              <Text size="sm">Download</Text>
            </MenuItem>
          )}
          emptyState={<DataTableEmpty message="No attachments yet" />}
        />
      </Box>
      {items.length > visibleRows.length ? (
        <Box direction="row" align="center" gap="sm" paddingLeft="sm">
          <Text size="sm" variant="muted">+{items.length - visibleRows.length} more</Text>
        </Box>
      ) : null}
      <MediaLightbox
        media={lightboxMedia}
        open={lightboxIndex !== null}
        onOpenChange={(open) => {
          if (!open) setLightboxIndex(null);
        }}
        initialIndex={lightboxIndex ?? 0}
        title="Attachments"
        downloadPrefix={entityName}
      />
    </Box>
  );
}
